import { useTranslation } from 'react-i18next';
import { TYPE } from '@/theme/typography';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Avatar, Menu, Divider,
  ListItemIcon, ListItemText, MenuItem, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Person as PersonIcon, Settings as SettingsIcon, Search as SearchIcon,
  Brightness4 as DarkIcon, Brightness7 as LightIcon,
  Language as LanguageIcon, Article as ArticleIcon,
  AdminPanelSettings as AdminIcon, Tune as ModuleSetupIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { portalApi } from '@/api/endpoints/hr';
import { resolveUploadUrl } from '@/utils/format';
import { useUIStore } from '@/stores/uiStore';
import { getPaletteByKey, getSidebarColors } from '@/config/colorPalettes';
import { MARKETING_URL, BLOG_URL } from '@/config/urls';
import { NAVBAR_COLORS, NEUTRAL_COLORS, IMPERSONATION_COLORS } from '@/theme/colorTokens';
import { hexToRgba } from '@/hooks/usePaletteAccents';
import { authApi } from '@/api/endpoints/auth';
import NotificationDropdown from './NotificationDropdown';
import AIQuotaChip from '@/components/common/AIQuotaChip';
import { LanguageFlag } from './LanguageFlag';

interface NavbarProps {
  companyColour: string;
}

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export default function Navbar({ companyColour }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  // Lightweight, cached read of the signed-in user's own profile photo so the
  // avatar shows their picture (not just initials) across every page. Shares
  // the query cache with the profile page; falls back to initials silently.
  const { data: myProfile } = useQuery({
    queryKey: ['profile/my-avatar'],
    queryFn: () => portalApi.getMyProfile().then((r) => r.data).catch(() => null),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const avatarSrc = resolveUploadUrl((myProfile as any)?.avatar_url);
  const platformRole = useAuthStore((s) => s.platformRole);
  const darkMode = useUIStore((s) => s.darkMode);
  const toggleDarkMode = useUIStore((s) => s.toggleDarkMode);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const toggleMobileSidebar = useUIStore((s) => s.toggleMobileSidebar);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  // On mobile the hamburger opens/closes the slide-in drawer; on desktop it
  // collapses the sidebar to a mini-rail. Same button, viewport-aware.
  const handleHamburger = () => {
    if (isMobile) toggleMobileSidebar();
    else toggleSidebar();
  };
  const sidebarStyle = useUIStore((s) => s.sidebarStyle);
  const paletteKey = useUIStore((s) => s.groupPaletteKey);
  const palette = getPaletteByKey(paletteKey);
  const sc = getSidebarColors(palette, sidebarStyle, darkMode);
  const isInverted = sc.isInverted;

  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } finally {
      logout();
      navigate('/login');
    }
  };

  const currentLangObj = LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        // Light sidebar: solid paper bg with company color bar (classic)
        // Colored/dark sidebar: frosted glass that blends with content
        // area — Linear/Vercel pattern
        bgcolor: isInverted
          ? hexToRgba(darkMode ? NAVBAR_COLORS.bgDark : palette.background, 0.82)
          : 'background.paper',
        backdropFilter: isInverted ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: isInverted ? 'blur(12px)' : 'none',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: isInverted
          ? hexToRgba(darkMode ? NAVBAR_COLORS.borderDark : NAVBAR_COLORS.borderLight, 0.5)
          : 'divider',
        borderTop: isInverted ? 'none' : `4px solid ${companyColour}`,
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease',
      }}
    >
      <Toolbar sx={{ gap: 2, pr: 2 }}>
        {/* Left: sidebar toggle — sits at the start of the navbar (to the right of
            the sidebar), so it no longer floats over and blocks the company logo. */}
        <Box
          component="button"
          onClick={handleHamburger}
          aria-label="Toggle sidebar"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
            width: 34,
            height: 34,
            p: 0,
            flexShrink: 0,
            cursor: 'pointer',
            bgcolor: NAVBAR_COLORS.hamburgerBg,
            border: `1px solid ${NAVBAR_COLORS.hamburgerBorder}`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            borderRadius: '6px',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: isInverted ? 'rgba(255,255,255,0.2)' : IMPERSONATION_COLORS.buttonHover },
          }}
        >
          <Box sx={{ width: 18, height: 3, bgcolor: NEUTRAL_COLORS.white, borderRadius: 1 }} />
          <Box sx={{ width: 18, height: 3, bgcolor: NEUTRAL_COLORS.white, borderRadius: 1 }} />
          <Box sx={{ width: 18, height: 3, bgcolor: NEUTRAL_COLORS.white, borderRadius: 1 }} />
        </Box>

        {/* Search relocated to the sidebar (below the Year selector). On phones
            the sidebar is a hidden drawer, so keep a compact search button here. */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        {/* Right: AI quota + Language + Theme + Notifications + User.
            gap reduced 10px→8px (icons 20% closer); mr nudges the whole
            cluster left off the right edge. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: 'auto', mr: 2 }}>
          <Tooltip title="Search">
            <IconButton
              onClick={() => setCommandPaletteOpen(true)}
              aria-label="Open search"
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                width: 36,
                height: 36,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '50%',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                '&:hover': { borderColor: 'text.secondary', color: 'text.primary' },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <AIQuotaChip />
          {/* Language — flag only (language code removed); button 15% smaller
              (36→31px). */}
          <Tooltip title={`Language: ${currentLangObj.label}`}>
            <IconButton
              size="small"
              onClick={(e) => setLangAnchorEl(e.currentTarget)}
              aria-label={`Language: ${currentLangObj.label}`}
              sx={{
                height: 31,
                px: 0.75,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                bgcolor: 'background.paper',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { borderColor: 'text.secondary' },
              }}
            >
              <LanguageFlag code={currentLangObj.code} size={18} />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={() => setLangAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={lang.code === i18n.language}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  localStorage.setItem('etg-language', lang.code);
                  setLangAnchorEl(null);
                }}
              >
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  <LanguageFlag code={lang.code} size={20} />
                </Box>
                <Typography variant="body2">{lang.label}</Typography>
              </MenuItem>
            ))}
          </Menu>

          {/* Theme Toggle — 15% smaller (36→31px, icon 20→17px) */}
          <Tooltip title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              onClick={toggleDarkMode}
              aria-label="Toggle dark mode"
              sx={{
                width: 31,
                height: 31,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '50%',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                '&:hover': { borderColor: 'text.secondary', color: 'text.primary' },
              }}
            >
              {darkMode
                ? <LightIcon sx={{ fontSize: 17 }} />
                : <DarkIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>

          {/* Module Setup — global module configuration, beside the bell */}
          <Tooltip title="Module Setup">
            <IconButton
              onClick={() => navigate('/settings/modules')}
              aria-label="Module Setup"
              sx={{
                width: 31,
                height: 31,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '50%',
                bgcolor: 'background.paper',
                color: 'text.secondary',
                '&:hover': { borderColor: 'text.secondary', color: 'text.primary' },
              }}
            >
              <ModuleSetupIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <NotificationDropdown />

          {/* User Menu */}
          <Tooltip title="Account">
            <IconButton onClick={(e) => setUserAnchorEl(e.currentTarget)} aria-label="User menu">
              <Avatar src={avatarSrc} sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: TYPE.base, fontWeight: TYPE.bold }}>
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={userAnchorEl}
            open={Boolean(userAnchorEl)}
            onClose={() => setUserAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            // Switcher menu text matches the sidebar nav menu item size
            // (TYPE.md = 0.9375rem / 15px), so both menus read identically.
            slotProps={{
              paper: {
                sx: {
                  '& .MuiListItemText-primary': { fontSize: TYPE.md },
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar src={avatarSrc} sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: TYPE.base, fontWeight: TYPE.bold }}>
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={TYPE.semibold} noWrap>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.67rem' }}>
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setUserAnchorEl(null); navigate('/profile'); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('nav.profile')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setUserAnchorEl(null); navigate('/settings'); }}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('nav.my_settings')}</ListItemText>
            </MenuItem>
            <Divider />
            {/* Cross-surface links — Marketing, Blog, Help Center always visible.
                Platform Admin only when the user has a platform role. All open
                in a new tab so in-app state is preserved. */}
            <MenuItem
              component="a"
              href={MARKETING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setUserAnchorEl(null)}
            >
              <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('nav.marketing_site', 'Marketing')}</ListItemText>
            </MenuItem>
            <MenuItem
              component="a"
              href={BLOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setUserAnchorEl(null)}
            >
              <ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('nav.blog', 'Blog')}</ListItemText>
            </MenuItem>
            {/* User Guide + Help Center moved into the unified HelpLauncher
                (bottom-left floating button) to avoid scattering help entries. */}
            {platformRole && (
              <MenuItem
                onClick={async () => {
                  setUserAnchorEl(null);
                  // Mint a fresh SSO hand-off token and redirect cross-origin
                  // to console.anoorehr.com so the founder doesn't see a
                  // second login screen. Falls back to the in-app
                  // /platform/overview shim if the API call fails or the
                  // backend hasn't been redeployed yet.
                  try {
                    const { data } = await authApi.platformSsoHandoff();
                    if (data?.url) {
                      window.location.href = data.url;
                      return;
                    }
                  } catch {
                    /* fall through to the shim path below */
                  }
                  try { await authApi.exitTenant(); } catch { /* ignore */ }
                  useAuthStore.getState().clearActiveCompany();
                  const landing =
                    platformRole === 'platform_marketing'
                      ? '/platform/content'
                      : '/platform/overview';
                  navigate(landing);
                }}
              >
                <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('nav.platform_admin', 'Console')}</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('auth.logout')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
